import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import PopupModal from "../../PopupModal";
import LoadingView from "../../LoadingView";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { bulkUploadHiringResumes, bulkSaveHiringCandidates } from "../../../api/services/hroneApi";
import {
  buildCreateCandidatePayload,
  getAtsMatchBadgeClass,
  getAtsMatchLabel,
  normalizeApplication,
  normalizeParsedCandidate,
  validateZipContainsOnlyPdfs,
} from "../../../util/hiringUtils";
import "./index.css";

const ACCEPT = ".zip,.pdf";

const BulkUploadModal = ({ jobId, isOpen, onClose, onSaved }) => {
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const inputRef = useRef(null);
  const [step, setStep]               = useState("upload");
  const [isDragging, setIsDragging]   = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [rows, setRows]               = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving]       = useState(false);

  const reset = () => {
    setStep("upload");
    setSelectedFiles([]);
    setRows([]);
    setIsDragging(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (isUploading || isSaving) return;
    reset();
    onClose();
  };

  const validateFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return [];
    const valid = files.filter((file) => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      return ["zip", "pdf"].includes(ext);
    });
    if (valid.length !== files.length) {
      toast.error("Some files were skipped. Only ZIP and PDF are supported.");
    }
    return valid;
  };

  const handleFiles = async (fileList) => {
    const valid = validateFiles(fileList);
    if (!valid.length) return;

    const zipChecks = await Promise.all(
      valid.map((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return ext === "zip" ? validateZipContainsOnlyPdfs(file) : true;
      })
    );

    const accepted = valid.filter((_, i) => zipChecks[i]);
    const rejectedNames = valid.filter((_, i) => !zipChecks[i]).map((f) => f.name);
    if (rejectedNames.length) {
      toast.error(
        `${rejectedNames.join(", ")} skipped — ZIP files must contain only PDF resumes.`
      );
    }

    if (accepted.length) setSelectedFiles(accepted);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || isUploading) return;

    const fd = new FormData();
    selectedFiles.forEach((file) => {
      fd.append("resumes", file, file.name);
    });

    setIsUploading(true);
    try {
      const res  = await bulkUploadHiringResumes(jobId, fd);
      const data = await res.json();
      if (!data.status) throw new Error(data.message || data.detail || "Upload failed");

      const candidates = data.data?.candidates || data.data || [];
      const normalized = (Array.isArray(candidates) ? candidates : []).map((item, index) => {
        const parsed = normalizeParsedCandidate(item);
        return {
          id:           item.temp_id ?? item.tempId ?? index,
          selected:     true,
          resumeName:   parsed.resumeFileName || item.file_name || item.fileName || `Resume ${index + 1}`,
          name:         parsed.name,
          email:        parsed.email,
          phone:        parsed.phone,
          experience:   parsed.experience,
          atsScore:     parsed.atsScore,
          recommendation: parsed.atsMatchLabel || getAtsMatchLabel(parsed.atsScore),
          parseId:      parsed.parseId,
          tempId:       item.temp_id ?? item.tempId ?? null,
          raw:          parsed,
        };
      });

      if (!normalized.length) throw new Error("No candidates were parsed from the uploaded files");
      setRows(normalized);
      setStep("review");
    } catch (err) {
      if (!navigator.onLine) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.message || "Bulk upload failed");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const updateRow = (rowId, key, value) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row)));
  };

  const toggleRow = (rowId) => {
    setRows((prev) => prev.map((row) => (
      row.id === rowId ? { ...row, selected: !row.selected } : row
    )));
  };

  const toggleAll = (checked) => {
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  };

  const saveRows = async (selectedOnly) => {
    if (isSaving) return;
    const toSave = selectedOnly ? rows.filter((r) => r.selected) : rows;
    if (!toSave.length) {
      toast.error("Select at least one candidate to save");
      return;
    }

    const candidates = toSave.map((row) => buildCreateCandidatePayload({
      name:              row.name,
      email:             row.email,
      phone:             row.phone,
      skills:            row.raw?.skills || "",
      experience:        row.experience,
      education:         row.raw?.education || "",
      previousCompanies: row.raw?.previousCompanies || "",
      certifications:    row.raw?.certifications || "",
      projects:          row.raw?.projects || "",
      designation:       row.raw?.designation || "",
    }, { parseId: row.parseId, tempId: row.tempId }));

    setIsSaving(true);
    try {
      const res  = await bulkSaveHiringCandidates(jobId, { candidates });
      const data = await res.json();
      if (!data.status) throw new Error(data.message || data.detail || "Bulk save failed");

      const saved = (data.data?.applications || data.data?.candidates || data.data || [])
        .map(normalizeApplication)
        .filter(Boolean);
      onSaved(saved.length ? saved : toSave.map((r) => normalizeApplication({
        application_id: r.tempId,
        name: r.name,
        email: r.email,
        mobile: r.phone,
        total_experience: r.experience,
        ats_score: r.atsScore,
        ats_match_label: r.recommendation,
        status: "APPLIED",
      })));
      toast.success(`${toSave.length} candidate${toSave.length > 1 ? "s" : ""} saved`);
      reset();
      onClose();
    } catch (err) {
      if (!navigator.onLine) {
        toast.error("Network error. Your edits are preserved — please try again.");
      } else {
        toast.error(err.message || "Bulk save failed");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PopupModal
      showModal={isOpen}
      modalName={step === "upload" ? "Bulk Upload Resumes" : "Review Bulk Upload"}
      closeBtn={handleClose}
      width={step === "upload" ? "520px" : "960px"}
      paddingTop={step === "review" ? "10px" : undefined}
    >
      <div className="bulk-upload-modal" style={{ "--hiring-primary": primaryColor }}>
        {step === "upload" && (
          <>
            {isUploading ? (
              <div className="bulk-upload-loading">
                <LoadingView />
                <p>Uploading and parsing resumes…</p>
              </div>
            ) : (
              <>
                <p className="bulk-upload-hint">
                  Upload a ZIP archive (PDFs only) or multiple PDF files. Parsed candidates will appear for review before saving.
                </p>
                <div
                  className={`bulk-upload-dropzone${isDragging ? " bulk-upload-dropzone--active" : ""}`}
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    handleFiles(e.dataTransfer.files);
                  }}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                  <BsCloudUpload className="bulk-upload-icon" />
                  <p className="bulk-upload-drop-title">
                    {selectedFiles.length
                      ? `${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""} selected`
                      : "Drag & drop files here"}
                  </p>
                  <p className="bulk-upload-drop-sub">ZIP (PDFs only) or PDF — or click to browse</p>
                </div>
                {selectedFiles.length > 0 && (
                  <ul className="bulk-upload-file-list">
                    {selectedFiles.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name}</li>
                    ))}
                  </ul>
                )}
                <div className="bulk-upload-actions">
                  <SecondaryButton type="button" onClick={handleClose}>Cancel</SecondaryButton>
                  <PrimaryButton type="button" disabled={!selectedFiles.length} onClick={handleUpload}>
                    Upload &amp; Parse
                  </PrimaryButton>
                </div>
              </>
            )}
          </>
        )}

        {step === "review" && (
          <>
            <div className="bulk-upload-table-wrap">
              <table className="bulk-upload-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={rows.every((r) => r.selected)}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th>Resume</th>
                    <th>Candidate Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Experience</th>
                    <th>ATS Score</th>
                    <th>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={() => toggleRow(row.id)}
                        />
                      </td>
                      <td className="bulk-upload-resume-name">{row.resumeName}</td>
                      <td>
                        <input
                          className="bulk-upload-cell-input"
                          value={row.name}
                          onChange={(e) => updateRow(row.id, "name", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="bulk-upload-cell-input"
                          value={row.email}
                          onChange={(e) => updateRow(row.id, "email", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="bulk-upload-cell-input"
                          value={row.phone}
                          onChange={(e) => updateRow(row.id, "phone", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="bulk-upload-cell-input"
                          value={row.experience}
                          onChange={(e) => updateRow(row.id, "experience", e.target.value)}
                        />
                      </td>
                      <td>{row.atsScore}%</td>
                      <td>
                        <span className={getAtsMatchBadgeClass(row.recommendation)}>
                          {row.recommendation}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bulk-upload-actions">
              <SecondaryButton type="button" onClick={handleClose} disabled={isSaving}>
                Cancel
              </SecondaryButton>
              <SecondaryButton type="button" onClick={() => saveRows(true)} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save Selected"}
              </SecondaryButton>
              <PrimaryButton type="button" onClick={() => saveRows(false)} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save All"}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </PopupModal>
  );
};

export default BulkUploadModal;
